/**
 * Campaigns API client — MOCK.
 *
 * Every function mirrors an endpoint in the Targeted Campaigns requirements (section 13)
 * and answers from an in-memory store seeded with mock data. Going live means replacing
 * the function bodies with HTTP calls; sagas, reducers and screens do not change.
 */
import type {
  AudienceCounts, BrandCampaignSettings, Campaign, CampaignAction, CampaignPerformanceRow, CampaignReport, Channel,
  CreditHistoryEntry, CreditPack, Customer, InboundSms, OverviewStats, RecipientRow, RewardGrant, RuleGroup, Segment, TrackingSource,
} from '../../components/campaigns/data/types';
import {
  MOCK_BRAND_ID, knownMockOutletIds, buildMockAutomations, buildMockCampaigns, buildMockCreditHistory,
  buildMockInbound, buildMockRun, buildMockSources, hasRun, mockCreditPacks, mockSettings, totalSent,
} from '../../components/campaigns/data/mockCampaigns';
import type { MockRun } from '../../components/campaigns/data/mockCampaigns';
import { ACTION_LABELS, nextStatus, canEditContent } from '../../components/campaigns/utils/campaignStatus';
import { validateSource } from '../../components/campaigns/utils/sources';
import { DEFAULT_QUIET_END, DEFAULT_QUIET_START } from '../../components/campaigns/config';
import { mockCustomers } from '../../components/customers/data/mockCustomers';
import { mockSegments } from '../../components/customers/data/mockSegments';
import { isReachable } from '../customers/customerConsent';
import { EMPTY_AUDIENCE, countAudience, evaluateSegment, isBlocked, type CampaignHistory } from '../customers/customerSegmentEngine';
import { validateRules } from '../customers/customerSegmentRules';

/** The customers and segments the mock service answers from. */
export interface MockDirectory {
  customers: Customer[];
  segments: Segment[];
}

let directoryOverride: MockDirectory | null = null;

/**
 * MOCK ONLY. Customers and segments are kept by the Customers module. The live campaigns service
 * reads the same customer database, so this hand-over goes away with the mock.
 */
export function setMockDirectory(directory: MockDirectory | null) { directoryOverride = directory; }

const directory = (): MockDirectory => directoryOverride ?? { customers: mockCustomers, segments: mockSegments };

interface Db {
  campaigns: Campaign[];
  settings: BrandCampaignSettings;
  creditHistory: CreditHistoryEntry[];
  inbound: InboundSms[];
  sources: TrackingSource[];
  runs: Map<string, MockRun>;
  /** Which campaigns each customer was sent and which they redeemed. */
  history: CampaignHistory;
  /** When each `sending` campaign started, so the mock can finish it. */
  sendingSince: Map<string, number>;
}

let db: Db | null = null;
let mockDelayMs = 300;
let mockFailure = false;
let idSeq = 0;

/** How long a mock send stays in `sending` before it completes. */
const MOCK_SEND_DURATION_MS = 20_000;

/** Test and demo hooks. */
export function setMockDelay(ms: number) { mockDelayMs = ms; }
export function setMockFailure(fail: boolean) { mockFailure = fail; }
export function resetMockDb() { db = null; idSeq = 0; directoryOverride = null; }

const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value));
const nowIso = () => new Date().toISOString();
const newId = (prefix: string) => { idSeq += 1; return `${prefix}_${Date.now().toString(36)}${idSeq}`; };

function respond<T>(produce: () => T): Promise<T> {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      if (mockFailure) { reject(new Error('The campaigns service is unavailable. Try again.')); return; }
      try { resolve(clone(produce())); } catch (e) { reject(e); }
    }, mockDelayMs);
  });
}

function runFor(d: Db, campaign: Campaign): MockRun {
  const cached = d.runs.get(campaign.id);
  if (cached) return cached;
  const { customers, segments } = directory();
  const run = buildMockRun({ campaign, customers, segments, history: d.history, brandName: d.settings.brandName, smsCreditPriceCents: d.settings.smsCreditPriceCents, now: new Date() });
  d.runs.set(campaign.id, run);
  return run;
}

function applyRunTotals(d: Db, campaign: Campaign) {
  if (!hasRun(campaign)) return;
  const { report, recipients, grants } = runFor(d, campaign);
  for (const r of recipients) if (r.status === 'sent') remember(d.history.received, r.customerId, campaign.id);
  for (const g of grants) if (g.status === 'redeemed') remember(d.history.redeemed, g.customerId, campaign.id);
  campaign.audienceCount = report.audience;
  campaign.attributedRevenueCents = report.revenueCents;
  campaign.sentLast30Days = totalSent(report);
  campaign.revenueLast30DaysCents = report.revenueCents;
  campaign.spendCents = report.spendCents;
}

function remember(index: Record<string, string[]>, customerId: string, campaignId: string) {
  const list = index[customerId] ?? (index[customerId] = []);
  if (!list.includes(campaignId)) list.push(campaignId);
}

function getDb(): Db {
  if (db) return db;
  const now = new Date();
  const d: Db = {
    campaigns: [...buildMockCampaigns(now), ...buildMockAutomations()],
    settings: clone(mockSettings),
    creditHistory: buildMockCreditHistory(now),
    inbound: buildMockInbound(directory().customers, now),
    sources: buildMockSources(now),
    runs: new Map(),
    history: { received: {}, redeemed: {} },
    sendingSince: new Map(),
  };
  for (const c of d.campaigns) applyRunTotals(d, c);
  db = d;
  return d;
}

function findCampaign(d: Db, id: string): Campaign {
  const c = d.campaigns.find((x) => x.id === id);
  if (!c) throw new Error('Campaign not found.');
  return c;
}

/** Moves time-driven statuses forward: scheduled → sending at the scheduled time, sending → completed. */
function tick(d: Db) {
  const now = Date.now();
  for (const c of d.campaigns) {
    if (c.status === 'scheduled' && c.scheduledAt && new Date(c.scheduledAt).getTime() <= now) startSending(d, c);
    const since = d.sendingSince.get(c.id);
    if (c.status === 'sending' && since !== undefined && now - since >= MOCK_SEND_DURATION_MS) {
      c.status = 'completed';
      c.updatedAt = nowIso();
      d.sendingSince.delete(c.id);
      d.runs.delete(c.id);
      applyRunTotals(d, c);
    }
  }
}

/** CMP-08 — the audience is evaluated when the campaign enters `sending`. */
function startSending(d: Db, c: Campaign) {
  c.status = 'sending';
  c.lastSentAt = nowIso();
  c.updatedAt = c.lastSentAt;
  d.sendingSince.set(c.id, Date.now());
  d.runs.delete(c.id);
  applyRunTotals(d, c);
}

// ── Audience ──

export interface PreviewInput {
  rules?: RuleGroup;
  segmentId?: string | null;
  outletIds?: string[];
}

/** SEG-13 — counts for unsaved rules or a saved segment. */
export function previewAudience(input: PreviewInput): Promise<AudienceCounts> {
  return respond(() => {
    const d = getDb();
    const { customers, segments } = directory();
    const rules = input.rules ?? segments.find((s) => s.id === input.segmentId)?.rules;
    if (!rules || validateRules(rules).length > 0) return EMPTY_AUDIENCE;
    const matched = evaluateSegment(rules, customers, { now: new Date(), history: d.history }, knownMockOutletIds(input.outletIds ?? []));
    return countAudience(matched);
  });
}

// ── Campaigns ──

export function listCampaigns(): Promise<Campaign[]> {
  return respond(() => {
    const d = getDb();
    tick(d);
    return d.campaigns;
  });
}

export type CampaignInput = Omit<Campaign, 'id' | 'brandId' | 'createdAt' | 'updatedAt' | 'status' | 'createdBy' | 'approvedBy' | 'lastSentAt' | 'audienceCount' | 'attributedRevenueCents' | 'sentLast30Days' | 'revenueLast30DaysCents' | 'spendCents' | 'pausedFrom'> & { id?: string };

export function saveCampaign(input: CampaignInput): Promise<Campaign> {
  return respond(() => {
    const d = getDb();
    const at = nowIso();
    const { id, ...fields } = input;
    if (id) {
      const campaign = findCampaign(d, id);
      // CMP-02
      if (!canEditContent(campaign)) throw new Error('Only draft campaigns can be edited.');
      Object.assign(campaign, fields, { updatedAt: at });
      return campaign;
    }
    const campaign: Campaign = {
      ...fields,
      id: newId('cmp'), brandId: MOCK_BRAND_ID, createdAt: at, updatedAt: at,
      status: 'draft', createdBy: 'user_owner', approvedBy: null, lastSentAt: null,
      audienceCount: 0, attributedRevenueCents: 0, sentLast30Days: 0, revenueLast30DaysCents: 0, spendCents: 0, pausedFrom: null,
    };
    d.campaigns.unshift(campaign);
    return campaign;
  });
}

export function transitionCampaign(id: string, action: CampaignAction, scheduledAt?: string | null): Promise<Campaign> {
  return respond(() => {
    const d = getDb();
    const c = findCampaign(d, id);
    let target = nextStatus(c.kind, c.status, action);
    // CMP-01
    if (!target) throw new Error(`"${ACTION_LABELS[action]}" is not available for a ${c.status} campaign.`);
    // AUT-13
    if ((action === 'enable' || (action === 'resume' && c.kind === 'automation'))
      && d.campaigns.some((x) => x.id !== c.id && x.kind === 'automation' && x.triggerType === c.triggerType && x.status === 'active')) {
      throw new Error('Another automation with this trigger is already active.');
    }
    if (action === 'resume' && c.pausedFrom) target = c.pausedFrom;
    if (action === 'pause') c.pausedFrom = c.status === 'sending' ? 'sending' : 'active';
    if (action === 'schedule') {
      if (!scheduledAt) throw new Error('Choose a send time.');
      c.scheduledAt = scheduledAt;
    }
    if (action === 'unschedule') c.scheduledAt = null;
    if (action === 'schedule' || action === 'send_now' || action === 'enable') c.approvedBy = 'user_owner';
    d.sendingSince.delete(c.id);
    if (target === 'sending') {
      if (action === 'resume') { c.status = 'sending'; d.sendingSince.set(c.id, Date.now()); } else startSending(d, c);
    } else {
      c.status = target;
    }
    if (action !== 'pause') c.pausedFrom = null;
    c.updatedAt = nowIso();
    if (c.kind === 'automation') { d.runs.delete(c.id); applyRunTotals(d, c); }
    return c;
  });
}

/** CMP-06 */
export function duplicateCampaign(id: string): Promise<Campaign> {
  return respond(() => {
    const d = getDb();
    const source = findCampaign(d, id);
    const at = nowIso();
    const copy: Campaign = {
      ...clone(source),
      id: newId('cmp'), createdAt: at, updatedAt: at, name: `${source.name} - copy`,
      status: 'draft', scheduledAt: null, approvedBy: null, lastSentAt: null,
      audienceCount: 0, attributedRevenueCents: 0, sentLast30Days: 0, revenueLast30DaysCents: 0, spendCents: 0, pausedFrom: null,
    };
    d.campaigns.unshift(copy);
    return copy;
  });
}

export function deleteCampaign(id: string): Promise<{ id: string }> {
  return respond(() => {
    const d = getDb();
    const c = findCampaign(d, id);
    if (c.status !== 'draft' || c.kind !== 'one_time') throw new Error('Only draft campaigns can be deleted.');
    d.campaigns = d.campaigns.filter((x) => x.id !== id);
    return { id };
  });
}

/** CMP-07 — delivers a sample; creates no recipients, grants or report data. */
export function testSend(_campaignId: string | null, channel: Channel, destination: string): Promise<{ channel: Channel; destination: string }> {
  return respond(() => ({ channel, destination }));
}

/** OFR-13 — revoke all unredeemed grants of a campaign. */
export function revokeGrants(campaignId: string): Promise<{ campaignId: string; revoked: number }> {
  return respond(() => {
    const d = getDb();
    const run = runFor(d, findCampaign(d, campaignId));
    let revoked = 0;
    for (const g of run.grants) {
      if (g.status === 'active') { g.status = 'revoked'; g.updatedAt = nowIso(); revoked += 1; }
    }
    run.report.unredeemedGrants = 0;
    return { campaignId, revoked };
  });
}

export interface ReportPayload { report: CampaignReport; recipients: RecipientRow[] }

export function getCampaignReport(campaignId: string): Promise<ReportPayload> {
  return respond(() => {
    const d = getDb();
    tick(d);
    const campaign = findCampaign(d, campaignId);
    const run = runFor(d, campaign);
    // RPT-15
    run.report.updatedAt = nowIso();
    return { report: run.report, recipients: run.recipients };
  });
}

// ── Rewards and campaign history per customer ──

export interface CustomerActivityPayload {
  grants: RewardGrant[];
  receivedByCustomer: Record<string, string[]>;
  redeemedByCustomer: Record<string, string[]>;
}

function activityOf(d: Db): CustomerActivityPayload {
  return {
    grants: d.campaigns.filter(hasRun).flatMap((c) => runFor(d, c).grants),
    receivedByCustomer: d.history.received,
    redeemedByCustomer: d.history.redeemed,
  };
}

/** Reward grants and campaign history per customer, as the customer profile shows them. */
export function listCustomerActivity(): Promise<CustomerActivityPayload> {
  return respond(() => activityOf(getDb()));
}

/** LNK-06 — when profiles are merged, their rewards and campaign history move to the kept profile. */
export function reassignCustomers(fromIds: string[], toId: string): Promise<CustomerActivityPayload> {
  return respond(() => {
    const d = getDb();
    const moved = new Set(fromIds);
    d.campaigns.filter(hasRun).forEach((c) => {
      const run = runFor(d, c);
      run.grants.forEach((g) => { if (moved.has(g.customerId)) g.customerId = toId; });
      run.recipients.forEach((r) => { if (moved.has(r.customerId)) r.customerId = toId; });
    });
    for (const index of [d.history.received, d.history.redeemed]) {
      for (const id of fromIds) {
        (index[id] ?? []).forEach((campaignId) => remember(index, toId, campaignId));
        delete index[id];
      }
    }
    return activityOf(d);
  });
}

// ── Settings ──

export interface SettingsPayload { settings: BrandCampaignSettings; creditHistory: CreditHistoryEntry[]; creditPacks: CreditPack[] }

export function getSettings(): Promise<SettingsPayload> {
  return respond(() => {
    const d = getDb();
    return { settings: d.settings, creditHistory: d.creditHistory, creditPacks: mockCreditPacks };
  });
}

/**
 * Buys a credit pack. MOCK: no payment is taken; the credits are added straight away.
 */
export function buyCredits(packId: string): Promise<SettingsPayload> {
  return respond(() => {
    const d = getDb();
    const pack = mockCreditPacks.find((p) => p.id === packId);
    if (!pack) throw new Error('That credit pack is no longer available.');
    const at = nowIso();
    d.settings.smsCreditsBalance += pack.credits;
    d.settings.updatedAt = at;
    d.creditHistory.unshift({
      id: newId('crd'), brandId: MOCK_BRAND_ID, createdAt: at, updatedAt: at,
      at, type: 'purchase', credits: pack.credits, balanceAfter: d.settings.smsCreditsBalance,
      note: `Credit pack: ${pack.credits.toLocaleString('en-US')}`,
    });
    return { settings: d.settings, creditHistory: d.creditHistory, creditPacks: mockCreditPacks };
  });
}

export type SettingsInput = Pick<BrandCampaignSettings,
  'frequencyCapCount' | 'frequencyCapDays' | 'quietStartLocal' | 'quietEndLocal'
  | 'emailFromName' | 'emailReplyTo' | 'postalAddress' | 'defaultAttributionWindowDays' | 'automationCheckTime'>;

export function saveSettings(input: SettingsInput): Promise<BrandCampaignSettings> {
  return respond(() => {
    const d = getDb();
    // GRD-05
    if (input.frequencyCapCount < 1 || input.frequencyCapCount > 10) throw new Error('The frequency cap must be between 1 and 10 messages.');
    if (input.quietStartLocal > DEFAULT_QUIET_START || input.quietEndLocal < DEFAULT_QUIET_END) throw new Error('Quiet hours can only be made stricter than 9:00 PM to 8:00 AM.');
    Object.assign(d.settings, input, { updatedAt: nowIso() });
    return d.settings;
  });
}

// ── Opt-out review queue ──

export function listInbound(): Promise<InboundSms[]> {
  return respond(() => getDb().inbound);
}

export interface ResolveInboundResult {
  inbound: InboundSms;
  /** Set when the message was marked as an opt-out: the customer to opt out of marketing SMS (CON-07). */
  optOutCustomerId: string | null;
}

/** CON-09 — staff mark an inbound SMS as an opt-out (which then follows CON-07) or dismiss it. */
export function resolveInbound(id: string, resolution: 'opt_out' | 'dismiss'): Promise<ResolveInboundResult> {
  return respond(() => {
    const d = getDb();
    const inbound = d.inbound.find((m) => m.id === id);
    if (!inbound) throw new Error('Message not found.');
    inbound.status = resolution === 'opt_out' ? 'opted_out' : 'dismissed';
    inbound.updatedAt = nowIso();
    return { inbound, optOutCustomerId: resolution === 'opt_out' ? inbound.customerId : null };
  });
}

// ── Tracking sources ──

export function listSources(): Promise<TrackingSource[]> {
  return respond(() => getDb().sources);
}

export type SourceInput = Pick<TrackingSource, 'name' | 'platform' | 'note' | 'linkTarget' | 'promoCode' | 'offerId' | 'offerName'> & { id?: string; status?: TrackingSource['status'] };

const TOKEN_ALPHABET = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';

/** Creates or updates a source. A new source gets its own short link. */
export function saveSource(input: SourceInput): Promise<TrackingSource> {
  return respond(() => {
    const d = getDb();
    const { id, ...fields } = input;
    const errors = validateSource({ ...fields, id, promoCode: fields.promoCode ?? '' }, d.sources);
    const firstError = Object.values(errors)[0];
    if (firstError) throw new Error(firstError);
    const at = nowIso();
    const clean = { ...fields, name: fields.name.trim(), note: fields.note.trim(), promoCode: fields.promoCode || null };
    if (id) {
      const source = d.sources.find((s) => s.id === id);
      if (!source) throw new Error('Source not found.');
      Object.assign(source, clean, { updatedAt: at });
      return source;
    }
    const source: TrackingSource = {
      ...clean,
      id: newId('src'), brandId: MOCK_BRAND_ID, createdAt: at, updatedAt: at,
      status: clean.status ?? 'active',
      linkToken: Array.from({ length: 6 }, () => TOKEN_ALPHABET[Math.floor(Math.random() * TOKEN_ALPHABET.length)]).join(''),
      clicks: 0, orders: 0, codeRedemptions: 0, newCustomers: 0, revenueCents: 0, discountCents: 0, lastClickAt: null,
    };
    d.sources.unshift(source);
    return source;
  });
}

export function deleteSource(id: string): Promise<{ id: string }> {
  return respond(() => {
    const d = getDb();
    if (!d.sources.some((s) => s.id === id)) throw new Error('Source not found.');
    d.sources = d.sources.filter((s) => s.id !== id);
    return { id };
  });
}

// ── Overview (RPT-17) ──

export function getOverview(rangeDays: number): Promise<OverviewStats> {
  return respond(() => {
    const d = getDb();
    const cutoff = Date.now() - rangeDays * 86_400_000;
    const { customers } = directory();
    // Blocked customers are never sent a campaign, so they are not counted as reachable.
    const sendable = customers.filter((c) => !isBlocked(c));
    const sent = { sms: 0, email: 0, push: 0 };
    let revenueCents = 0;
    let spendCents = 0;
    let orders = 0;
    let optedOut = 0;
    let totalMessages = 0;
    const perDay = new Map<string, { sms: number; email: number; push: number }>();
    const performance: CampaignPerformanceRow[] = [];

    for (const c of d.campaigns.filter(hasRun)) {
      const sentAt = c.lastSentAt ? new Date(c.lastSentAt).getTime() : Date.now() - 5 * 86_400_000;
      if (sentAt < cutoff) continue;
      const { report } = runFor(d, c);
      const day = new Date(sentAt).toISOString().slice(0, 10);
      const bucket = perDay.get(day) ?? { sms: 0, email: 0, push: 0 };
      (['sms', 'email', 'push'] as Channel[]).forEach((ch) => {
        sent[ch] += report.perChannel[ch].sent;
        bucket[ch] += report.perChannel[ch].sent;
      });
      perDay.set(day, bucket);
      const campaignOrders = report.attributedOrders.redemption + report.attributedOrders.click + report.attributedOrders.window;
      revenueCents += report.revenueCents;
      spendCents += report.spendCents;
      orders += campaignOrders;
      optedOut += report.optedOut;
      totalMessages += totalSent(report);
      performance.push({
        id: c.id, name: c.name, kind: c.kind, sentAt: c.lastSentAt,
        sent: totalSent(report),
        delivered: report.perChannel.sms.delivered + report.perChannel.email.delivered + report.perChannel.push.delivered,
        clicked: report.uniqueClicks, redemptions: report.redemptions, optedOut: report.optedOut,
        orders: campaignOrders, spendCents: report.spendCents, revenueCents: report.revenueCents,
      });
    }

    const series = Array.from({ length: rangeDays }, (_, i) => {
      const date = new Date(Date.now() - (rangeDays - 1 - i) * 86_400_000).toISOString().slice(0, 10);
      return { date, ...(perDay.get(date) ?? { sms: 0, email: 0, push: 0 }) };
    });
    const at = nowIso();
    return {
      id: `ovw_${rangeDays}`, createdAt: at, updatedAt: at, rangeDays, sent, revenueCents, spendCents, orders,
      optOutRate: totalMessages > 0 ? optedOut / totalMessages : 0,
      // LNK-10 — linked completed orders divided by all completed orders, last 30 days
      linkedOrderRate: 0.684,
      reachable: {
        sms: sendable.filter((c) => isReachable(c, 'sms')).length,
        email: sendable.filter((c) => isReachable(c, 'email')).length,
        push: sendable.filter((c) => isReachable(c, 'push')).length,
      },
      totalCustomers: customers.length,
      series,
      campaigns: performance,
    };
  });
}
